import { memo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const headingVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const textVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const GRID_PATTERN =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzBoLTJWMGgydjMwem0tMiAwSDJ2LTJoMzJ2MnptMCAydjI4aC0yVjMyaDJ6TTMyIDMwSDAgMzJ6Ii8+PC9nPjwvZz48L3N2Zz4=";

function Hero({ heroImage }) {
  const { t } = useTranslation();
  const blogHeroData = t("components.blog.hero", { returnObjects: true });

  return (
    <div
      className="relative h-[85vh] landscape:max-lg:h-[125vh] w-full overflow-hidden"
      id="safePad"
    >
      <motion.div
        initial={{ scale: 1.2 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.5 }}
        className="absolute inset-0 overflow-hidden"
      >
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: `url(${heroImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "scroll",
            filter: "brightness(0.6)",
          }}
        />
        <div className="absolute inset-0 bg-linear-to-br from-black via-gray-900/80 to-blue-900 opacity-85" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.1 }}
        transition={{ duration: 2 }}
        className={`absolute inset-0 bg-[url('${GRID_PATTERN}')]`}
      />

      <div className="relative h-full max-w-7xl mx-auto px-4 flex items-center justify-center">
        <div className="text-center space-y-6">
          <motion.h1
            variants={headingVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight"
          >
            {blogHeroData.heading}
          </motion.h1>

          <motion.p
            variants={textVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-md sm:text-lg md:text-xl lg:text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed"
          >
            {blogHeroData.description}
          </motion.p>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-24 bg-linear-to-t from-black to-transparent" />
    </div>
  );
}

Hero.propTypes = {
  heroImage: PropTypes.string.isRequired,
};

export default memo(Hero);
